import { useState, useEffect, useRef, useContext, useMemo } from "react";
import ReactDatePicker, { DateObject, getAllDatesInRange } from "react-multi-date-picker";
import { Box, Grid, Typography } from "@mui/material";
import persian from "react-date-object/calendars/persian";
import gregorian from "react-date-object/calendars/gregorian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian_en from "react-date-object/locales/gregorian_en";
import weekends from "react-multi-date-picker/plugins/highlight_weekends";
import { LocaleContext } from "../../../routing/LangRouter";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";
import { holidays } from "../../../constants/holidays";

//import general components
import BaseTextField from "../base-text-field/base-text-field";
import { BaseButton } from "../base-button/base-button";

//import images and icons
import { ArrowLeft2, ArrowRight2, CalendarTick, CloseCircle } from "iconsax-react";

//import styles
import "./range-date-picker.scss";
import { numberWithCommas } from "../../../utils/utility";

const CalendarToolbar = ({ handleCalendarClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    return (
        <Grid className={isMobile ? "toolbar toolbar--top" : ""} container item xs={12} justifyContent="flex-end">
            <Grid container item xs={1.5} justifyContent="flex-end">
                {IS_MOBILE && (
                    <BaseButton
                        color="transparent"
                        size="small"
                        radius="radius-10"
                        type="button"
                        handleClick={e => handleCalendarClose(e, "cancel")}
                        rightIcon={<CloseCircle size="24" color="var(--teal-color-18)" />}
                    />
                )}
            </Grid>
        </Grid>
    );
};

const ButtonsToolbar = ({ isPersianCalendar, handleCalendarToggle, handleCalendarClose, locale }) => {
    const { t } = useTranslation();
    const buttonText = isPersianCalendar ? t("Calendar.Gregorian Calendar") : t("Calendar.Jalali Calendar");
    return (
        <Grid className="toolbar toolbar--bottom" container item xs={12} justifyContent="space-between" display={locale === "fa" ? "flex" : "none"}>
            <Grid container item xs={4} md={2.5} xl={2}>
                <BaseButton
                    text={t("confirmation")}
                    color="primary"
                    size="small"
                    radius="radius-10"
                    type="button"
                    handleClick={e => handleCalendarClose(e, "confirm")}
                />
            </Grid>
            <Grid container item xs="auto">
                <BaseButton text={buttonText} color="outline" size="small" radius="radius-10" type="button" handleClick={handleCalendarToggle} />
            </Grid>
        </Grid>
    );
};

const RangeDatePicker = ({ handleChange, disableDate, defaultValue, labels, minDate, range, innerRef, register, error, priceList = [] }) => {
    const { locale } = useContext(LocaleContext);

    const priceMap = useMemo(() => {
        return new Map(priceList?.map(item => [item.departure_date, item.price === "-" ? "-" : item.price]));
    }, [priceList]);

    const minPrice = useMemo(() => {
        return Math?.min(priceList?.filter(item => item === "-").map(item => item.price));
    }, [priceList]);

    // Refs and state for managing date picker functionality
    const datePickerRef = useRef();
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [isPersianCalendar, setIsPersianCalendar] = useState(locale === "fa");
    const [isRangeMode, setIsRangeMode] = useState(range);

    // Week day names in Persian (fa) and English (en)
    const weekDays = {
        fa: ["ش", "ی", "د", "س", "چ", "پ", "ج"],
        en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };

    // Update selected date range and all dates when default value changes
    useEffect(() => {
        setSelectedDateRange(defaultValue || null);
        setAllDates(defaultValue ? [] : []);
        setIsRangeMode(range);
    }, [defaultValue, range]);

    // Update calendar type based on locale changes
    useEffect(() => {
        setIsPersianCalendar(locale === "fa");
    }, [locale]);

    // Handle date selection and update state with selected date range
    const handleDateSelection = selectedDates => {
        const dateRange = isRangeMode ? selectedDates : selectedDates.concat(selectedDates[0]);
        setSelectedDateRange(dateRange);
        setAllDates(getAllDatesInRange(dateRange));
    };

    // Handle date range changes on mobile devices by calling handleChange with selected dates
    useEffect(() => {
        if (selectedDateRange && IS_MOBILE) {
            handleChange(selectedDateRange, allDates, isPersianCalendar);
        }
    }, [selectedDateRange, IS_MOBILE, allDates, isPersianCalendar]);

    // Close calendar and hide container after date selection on desktop
    useEffect(() => {
        if ((!IS_MOBILE && allDates.length > 1 && isRangeMode) || (!IS_MOBILE && allDates.length === 1 && !isRangeMode)) {
            datePickerRef.current.closeCalendar();
            setTimeout(() => {
                document.querySelectorAll(".rmdp-container > div")[1].style.visibility = "hidden !important";
            }, 500);
        }
    }, [allDates, isRangeMode, IS_MOBILE]);

    // Toggle between Persian and Gregorian calendar views
    const handleCalendarToggle = event => {
        event.preventDefault();
        setIsPersianCalendar(!isPersianCalendar);
    };

    const handleCalendarClose = (event, type) => {
        event.preventDefault();
        datePickerRef.current.closeCalendar();
        if (type === "cancel") {
            handleChange(null, null, null);
            setSelectedDateRange(null);
        }
    };

    // Renders navigation arrow buttons for the date picker
    const renderDateButton = (direction, handleClick) => {
        const ArrowIcon = direction === "right" ? ArrowRight2 : ArrowLeft2;
        const shouldFlipArrow = locale === "fa" && isPersianCalendar;
        return (
            <span className="dates-container__date-button" onClick={handleClick}>
                <ArrowIcon color="var(--teal-color-18)" style={{ transform: shouldFlipArrow ? "rotate(180deg)" : "none" }} />
            </span>
        );
    };

    // Render the date input fields for the range date picker
    const renderDateFields = (value, onFocus) => (
        <Grid className="dates-container" container item xs={12} rowGap={1} justifyContent="space-between">
            <Grid container item xs={6}>
                <BaseTextField
                    label={labels?.[0]}
                    type="text"
                    name="startDate"
                    value={value?.split?.("~")?.[0] ?? ""}
                    onFocus={onFocus}
                    inputRef={innerRef}
                    size="medium"
                    radius="10px 0 0 10px"
                    disabled={disableDate}
                    readOnly={true}
                    register={register?.[0]}
                    error={error?.[0]}
                    icon={<CalendarTick size="20" color={disableDate ? "var(--gray-color)" : "var(--teal-color-18)"} />}
                />
            </Grid>
            <Grid container item xs={6}>
                <BaseTextField
                    label={labels?.[1]}
                    type="text"
                    name="endDate"
                    value={isRangeMode ? value?.split?.("~")?.[1] || "" : ""}
                    onFocus={onFocus}
                    size="medium"
                    radius="0 10px 10px 0"
                    disabled={!isRangeMode || disableDate}
                    readOnly={true}
                    register={register?.[1]}
                    error={error?.[1]}
                    icon={<CalendarTick size="20" color={disableDate ? "var(--gray-color)" : "var(--teal-color-18)"} />}
                />
            </Grid>
        </Grid>
    );

    return (
        <div className="calendar">
            <ReactDatePicker
                className={`${isPersianCalendar ? "fa" : "en"}`}
                // containerStyle={{ width: "100px" }}
                ref={datePickerRef}
                value={selectedDateRange}
                range
                rangeHover
                mapDays={({ date }) => {
                    // Convert to Gregorian format
                    let formatted = new DateObject({
                        date,
                        calendar: gregorian,
                    }).format("YYYY-MM-DD");

                    // Convert Persian digits to English
                    formatted = formatted.replace(/[۰-۹]/g, d => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));

                    const price = priceMap.get(formatted) ?? "-";
                    const isMin = minPrice == price;

                    return {
                        style: { padding: 0 },
                        children: (
                            <>
                                <Typography
                                    className={`${isPersianCalendar && holidays.includes(formatted) && "highlight highlight-red"}`}
                                    sx={{ fontSize: "14px" }}
                                >
                                    {date.day}
                                </Typography>

                                {!!priceList?.length && (
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: `${isMin ? "var(--primary-color)" : "inherit"}`,
                                            textDecoration: "none",
                                        }}
                                    >
                                        {numberWithCommas(price)}
                                    </Typography>
                                )}
                            </>
                        ),
                    };
                }}
                locale={isPersianCalendar ? persian_fa : gregorian_en}
                calendar={isPersianCalendar ? persian : gregorian}
                weekDays={weekDays[isPersianCalendar ? "fa" : "en"]}
                minDate={minDate}
                numberOfMonths={2}
                arrow={false}
                disabled={disableDate}
                fixMainPosition={true}
                calendarPosition="bottom-right"
                onChange={dateObjects => {
                    handleDateSelection(dateObjects);
                }}
                onClose={() => {
                    handleChange?.(selectedDateRange, allDates, isPersianCalendar);
                }}
                plugins={[
                    weekends(isPersianCalendar ? [6] : [0, 6]),
                    <CalendarToolbar position="top" handleCalendarClose={handleCalendarClose} />,
                    <ButtonsToolbar
                        position="bottom"
                        handleCalendarClose={handleCalendarClose}
                        isPersianCalendar={isPersianCalendar}
                        handleCalendarToggle={handleCalendarToggle}
                        locale={locale}
                    />,
                ]}
                renderButton={renderDateButton}
                render={renderDateFields}
            />
        </div>
    );
};

export default RangeDatePicker;
